import AnnotationOverlay from "./AnnotationOverlay";
import PlacedVariableMarker from "./PlacedVariableMarker";
import { ANNOTATION_TOOLS } from "./constants";
import { Alert, Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import Button from "components/common/Button";
import { renderAsync } from "docx-preview";
import { useRef, useState, useEffect } from "react";
import { getPdfDocument, loadPdfJsLib } from "services/pdfjs";

// Base on-screen width for each rendered PDF page (CSS pixels). The canvas
// bitmap is rendered at a higher device-pixel ratio so it stays crisp on
// retina displays.
const PDF_BASE_WIDTH = 650;

const DocumentPreviewCanvas = ({
  uploadedFile,
  // When set + useServerSidePdfPreview is true the docx will be converted
  // server-side via LibreOffice and rendered as a PDF, matching the fidelity
  // of the system's published documents. Without this we fall back to
  // client-side docx-preview which has known fidelity gaps.
  s3FileId = null,
  useServerSidePdfPreview = false,
  convertFileToPdfFn = null,
  placements,
  onAddPlacement,
  onRemovePlacement,
  onMovePlacement,
  onEditCondition,
  zoom,
  annotations,
  onAddAnnotation,
  onRemoveAnnotation,
  onMoveAnnotation,
  onResizeAnnotation,
  onRequestComment,
}) => {
  const theme = useTheme();
  const canvasRef = useRef(null);
  const docContainerRef = useRef(null);
  const pdfUrlRef = useRef(null);
  // Refs for each rendered PDF page's wrapper Box + inner <canvas>. These
  // line up by index with `pdfPages` (page 1 is index 0).
  const pageWrapperRefs = useRef([]);
  const pageCanvasRefs = useRef([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragType, setDragType] = useState(null); // "variable" or "annotation"
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMode, setLoadingMode] = useState(null);
  const [loadingElapsed, setLoadingElapsed] = useState(0);
  const [error, setError] = useState(null);
  const [isRendered, setIsRendered] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  // Bumped when the user clicks Retry on the error state. Wired into the
  // load effect's deps so the convert is re-attempted; on cache hit (Lambda
  // already finished even though the API Gateway gave up at 30s) this
  // typically returns in 100-300ms.
  const [retryCounter, setRetryCounter] = useState(0);
  // One entry per PDF page after the document is parsed; carries the page
  // number and base viewport dimensions so we can size the wrapper Box
  // before pdfjs has painted into the canvas.
  const [pdfPages, setPdfPages] = useState([]);

  const isPdf =
    uploadedFile?.name?.toLowerCase().endsWith(".pdf") ||
    uploadedFile?.type === "application/pdf";

  useEffect(() => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
      setPdfUrl(null);
    }

    if (!uploadedFile) {
      setIsRendered(false);
      setError(null);
      setPdfPages([]);
      if (docContainerRef.current) {
        docContainerRef.current.innerHTML = "";
      }
      return;
    }

    if (isPdf) {
      setIsLoading(true);
      setLoadingMode("pdf");
      setError(null);
      setPdfPages([]);
      setIsRendered(false);
      try {
        const url = URL.createObjectURL(uploadedFile);
        pdfUrlRef.current = url;
        setPdfUrl(url);
      } catch {
        setError("Failed to load PDF. Please try again.");
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Server-side preview path. When the flag is on we commit to the
    // server-converted PDF and never render the lower-fidelity docx-preview
    // — even during the gap between the file being dropped and the upload
    // assigning s3FileId. Otherwise the user sees a brief flash of the
    // client-side render before the PDF takes over, which looked jarring.
    if (useServerSidePdfPreview) {
      setIsLoading(true);
      setLoadingMode("server-pdf");
      setError(null);
      setIsRendered(false);
      setPdfPages([]);

      if (!s3FileId || !convertFileToPdfFn) {
        // Still waiting on the upload's file_id (or the mutation hook
        // wasn't supplied). Keep the loading overlay up; this effect will
        // re-run once s3FileId arrives and actually fire the convert.
        return;
      }

      let cancelled = false;
      (async () => {
        try {
          const result = await convertFileToPdfFn({
            fileId: s3FileId,
          }).unwrap();
          if (cancelled) return;
          if (result?.pdfDownloadUrl) {
            setPdfUrl(result.pdfDownloadUrl);
          } else {
            setError("Server-side PDF conversion returned no URL.");
          }
        } catch {
          if (!cancelled) {
            setError(
              "Could not render this template via the server-side PDF preview."
            );
          }
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    if (!docContainerRef.current) return;

    const renderDocument = async () => {
      setIsLoading(true);
      setLoadingMode("client-docx");
      setError(null);
      setIsRendered(false);

      try {
        docContainerRef.current.innerHTML = "";
        await renderAsync(uploadedFile, docContainerRef.current, null, {
          className: "docx-preview",
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: false,
          experimental: true,
          trimXmlDeclaration: true,
          useBase64URL: true,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
        });
        setIsRendered(true);
      } catch {
        setError(
          "Failed to render document. Please ensure it's a valid Word document (.docx)."
        );
      } finally {
        setIsLoading(false);
      }
    };

    renderDocument();

    return () => {
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
    };
  }, [
    uploadedFile,
    isPdf,
    s3FileId,
    useServerSidePdfPreview,
    convertFileToPdfFn,
    retryCounter,
  ]);

  // pdfjs path A: once we have a pdfUrl, parse the document and capture
  // each page's base viewport. The `pdfDoc` itself goes into a ref (not
  // state) so React doesn't try to serialise it.
  const pdfDocRef = useRef(null);
  useEffect(() => {
    if (!pdfUrl) {
      setPdfPages([]);
      pdfDocRef.current = null;
      return;
    }
    let cancelled = false;
    let loadingTask = null;
    (async () => {
      try {
        const pdfjsLib = await loadPdfJsLib();
        if (cancelled || !pdfjsLib) return;
        loadingTask = getPdfDocument(pdfjsLib, pdfUrl);
        const pdf = await loadingTask.promise;
        if (cancelled) return;

        const pages = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const baseViewport = page.getViewport({ scale: 1 });
          pages.push({
            pageNumber: i,
            baseWidth: baseViewport.width,
            baseHeight: baseViewport.height,
          });
        }
        if (cancelled) return;
        pdfDocRef.current = pdf;
        setPdfPages(pages);
      } catch (e) {
        if (!cancelled) {
          setError("Failed to render the PDF preview.");
        }
      }
    })();
    return () => {
      cancelled = true;
      if (loadingTask) loadingTask.destroy?.();
    };
  }, [pdfUrl]);

  // pdfjs path B: once `pdfPages` is set, React has rendered the page
  // wrappers and the canvas refs are populated. Paint each page now.
  // Render at devicePixelRatio above the CSS size for crisp output on
  // retina displays.
  useEffect(() => {
    const pdf = pdfDocRef.current;
    if (!pdf || pdfPages.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const dpr = window.devicePixelRatio || 1;
        for (let i = 0; i < pdfPages.length; i++) {
          if (cancelled) break;
          const canvas = pageCanvasRefs.current[i];
          if (!canvas) continue;
          const page = await pdf.getPage(i + 1);
          const baseViewport = page.getViewport({ scale: 1 });
          const cssScale = PDF_BASE_WIDTH / baseViewport.width;
          const viewport = page.getViewport({ scale: cssScale * dpr });
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = `${PDF_BASE_WIDTH}px`;
          canvas.style.height = `${baseViewport.height * cssScale}px`;
          const ctx = canvas.getContext("2d");
          await page.render({ canvasContext: ctx, viewport }).promise;
        }
        if (!cancelled) setIsRendered(true);
      } catch {
        if (!cancelled) setError("Failed to render the PDF preview.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfPages]);

  // Tick an elapsed-seconds counter while loading. Gives the user something
  // to look at and an honest sense of progress, since server-side conversion
  // can sit at 10-15s on a cold worker container.
  useEffect(() => {
    if (!isLoading) {
      setLoadingElapsed(0);
      return;
    }
    const startedAt = Date.now();
    const id = setInterval(() => {
      setLoadingElapsed(Math.round((Date.now() - startedAt) / 1000));
    }, 250);
    return () => clearInterval(id);
  }, [isLoading]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";

    // Determine what type of item is being dragged
    const types = e.dataTransfer.types;
    if (types.includes("application/annotation")) {
      setDragType("annotation");
    } else if (types.includes("application/json")) {
      setDragType("variable");
    }

    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    setDragType(null);
  };

  // Shared drop handler. `targetRect` is the bounding rect of the element
  // we're dropping onto (a specific PDF page wrapper, or the docx-preview
  // container). `pageNumber` is recorded on the placement/annotation so a
  // future renderer can paint each marker on the correct page.
  const dropOnto = (e, targetRect, pageNumber) => {
    e.preventDefault();
    setIsDragOver(false);
    setDragType(null);

    const x = ((e.clientX - targetRect.left) / targetRect.width) * 100;
    const y = ((e.clientY - targetRect.top) / targetRect.height) * 100;
    const position = {
      x: Math.max(2, Math.min(98, x)),
      y: Math.max(1, Math.min(99, y)),
    };

    try {
      const annotationData = e.dataTransfer.getData("application/annotation");
      if (annotationData) {
        const { type } = JSON.parse(annotationData);

        if (type === "comment") {
          onRequestComment?.({ ...position, page: pageNumber });
          return;
        }

        const tool = ANNOTATION_TOOLS.find((t) => t.id === type);
        onAddAnnotation?.({
          id: `annotation-${Date.now()}`,
          type,
          x: position.x,
          y: position.y,
          page: pageNumber,
          color: tool?.color || "#666666",
          width:
            type === "highlight"
              ? 100
              : type === "box"
              ? 80
              : type === "line" || type === "arrow"
              ? 80
              : 60,
          height: type === "highlight" ? 24 : type === "box" ? 50 : 60,
        });
        return;
      }
    } catch {
      // Not an annotation, try variable
    }

    try {
      const variableData = e.dataTransfer.getData("application/json");
      if (variableData) {
        const variable = JSON.parse(variableData);
        onAddPlacement({
          id: `${variable.path}-${Date.now()}`,
          variable,
          x: position.x,
          y: position.y,
          page: pageNumber,
        });
      }
    } catch {
      // Invalid drop data - silently ignore
    }
  };

  const handlePageDrop = (e, pageNumber, index) => {
    const wrapper = pageWrapperRefs.current[index];
    if (!wrapper) return;
    dropOnto(e, wrapper.getBoundingClientRect(), pageNumber);
  };

  const handleDocDrop = (e) => {
    const docWrapper =
      docContainerRef.current?.querySelector(".docx-wrapper") ||
      docContainerRef.current;
    const rect =
      docWrapper?.getBoundingClientRect() ||
      canvasRef.current.getBoundingClientRect();
    dropOnto(e, rect, 1);
  };

  return (
    <Box
      ref={canvasRef}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={pdfUrl ? undefined : handleDocDrop}
      sx={{
        flex: 1,
        overflow: "auto",
        backgroundColor: theme.palette.grey[400],
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: 2,
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.9)",
            zIndex: 30,
          }}
        >
          <Stack spacing={2} alignItems={"center"} sx={{ maxWidth: 360 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                border: `4px solid ${theme.palette.grey[300]}`,
                borderTop: `4px solid ${theme.palette.primary.main}`,
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            />
            <Typography
              color={"text.primary"}
              fontWeight={600}
              textAlign={"center"}
            >
              {loadingMode === "server-pdf"
                ? "Generating high-fidelity preview"
                : "Rendering document..."}
            </Typography>
            {loadingMode === "server-pdf" && (
              <Typography
                variant={"body2"}
                color={"text.secondary"}
                textAlign={"center"}
              >
                {loadingElapsed < 6
                  ? "Converting your document the same way Cactus generates final PDFs. This takes a few seconds."
                  : loadingElapsed < 15
                  ? "Still working — first preview of the day takes a little longer while the converter warms up."
                  : "Almost there. Hang tight."}
              </Typography>
            )}
            {loadingMode === "server-pdf" && loadingElapsed > 0 && (
              <Typography
                variant={"caption"}
                color={"text.secondary"}
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {`${loadingElapsed}s elapsed`}
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      {error && !isLoading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 400,
            width: "100%",
          }}
        >
          <Alert
            severity={"error"}
            sx={{ maxWidth: 400 }}
            action={
              loadingMode === "server-pdf" ? (
                <Button
                  size={"small"}
                  variant={"outlined"}
                  onClick={() => {
                    setError(null);
                    setRetryCounter((n) => n + 1);
                  }}
                >
                  {"Retry"}
                </Button>
              ) : undefined
            }
          >
            {error}
            {loadingMode === "server-pdf" && (
              <Typography
                variant={"caption"}
                color={"text.secondary"}
                sx={{ display: "block", mt: 0.5 }}
              >
                {
                  "The converter may have finished after the initial timeout. Retry usually returns from cache instantly."
                }
              </Typography>
            )}
          </Alert>
        </Box>
      )}

      <Box
        sx={{
          position: "relative",
          transform: `scale(${zoom})`,
          transformOrigin: "top center",
          transition: "transform 0.2s ease",
          // Disable text selection in document preview
          userSelect: "none",
          "& *": {
            userSelect: "none !important",
          },
          "& .docx-wrapper": {
            background: "transparent !important",
            padding: "0 !important",
          },
          "& .docx": {
            boxShadow: "0 4px 20px rgba(0,0,0,0.15) !important",
            margin: "16px auto !important",
            backgroundColor: "white !important",
          },
          "& section.docx": {
            marginBottom: "24px !important",
          },
        }}
      >
        {pdfUrl && (
          <Stack spacing={3} sx={{ alignItems: "center", py: 2 }}>
            {pdfPages.map((p, index) => {
              const pageNumber = p.pageNumber;
              const pageHeight = (p.baseHeight * PDF_BASE_WIDTH) / p.baseWidth;
              const pagePlacements = placements.filter(
                (pl) => (pl.page ?? 1) === pageNumber
              );
              const pageAnnotations = (annotations || []).filter(
                (a) => (a.page ?? 1) === pageNumber
              );
              const wrapperGetter = () => pageWrapperRefs.current[index];
              return (
                <Box
                  key={pageNumber}
                  ref={(el) => (pageWrapperRefs.current[index] = el)}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handlePageDrop(e, pageNumber, index)}
                  sx={{
                    position: "relative",
                    width: PDF_BASE_WIDTH,
                    height: pageHeight,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                    backgroundColor: "white",
                  }}
                >
                  <canvas
                    ref={(el) => (pageCanvasRefs.current[index] = el)}
                    style={{
                      display: "block",
                      width: "100%",
                      height: "100%",
                    }}
                  />
                  {isRendered && pagePlacements.length > 0 && (
                    <Box
                      data-variable-marker={"true"}
                      sx={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        zIndex: 10,
                      }}
                    >
                      {pagePlacements.map((placement) => (
                        <Box
                          key={placement.id}
                          data-variable-marker={"true"}
                          sx={{
                            position: "absolute",
                            left: `${placement.x}%`,
                            top: `${placement.y}%`,
                            transform: "translate(-50%, -50%)",
                            pointerEvents: "auto",
                          }}
                        >
                          <PlacedVariableMarker
                            placement={placement}
                            onRemove={onRemovePlacement}
                            onMove={onMovePlacement}
                            onEditCondition={onEditCondition}
                            zoom={zoom}
                            containerRef={{ current: wrapperGetter() }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                  {isRendered && pageAnnotations.length > 0 && (
                    <Box
                      data-annotation={"true"}
                      sx={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        zIndex: 20,
                      }}
                    >
                      {pageAnnotations.map((annotation) => (
                        <Box
                          key={annotation.id}
                          data-annotation={"true"}
                          sx={{ pointerEvents: "auto" }}
                        >
                          <AnnotationOverlay
                            annotation={annotation}
                            onRemove={onRemoveAnnotation}
                            onMove={onMoveAnnotation}
                            onResize={onResizeAnnotation}
                            zoom={zoom}
                            containerRef={{ current: wrapperGetter() }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}

        {!pdfUrl && (
          <Box
            ref={docContainerRef}
            sx={{
              minWidth: 600,
              minHeight: isRendered ? "auto" : 800,
            }}
          />
        )}

        {!pdfUrl && isRendered && placements.length > 0 && (
          <Box
            data-variable-marker={"true"}
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
            }}
          >
            {placements.map((placement) => (
              <Box
                key={placement.id}
                data-variable-marker={"true"}
                sx={{
                  position: "absolute",
                  left: `${placement.x}%`,
                  top: `${placement.y}%`,
                  transform: "translate(-50%, -50%)",
                  pointerEvents: "auto",
                  zIndex: 10,
                }}
              >
                <PlacedVariableMarker
                  placement={placement}
                  onRemove={onRemovePlacement}
                  onMove={onMovePlacement}
                  onEditCondition={onEditCondition}
                  zoom={zoom}
                  containerRef={docContainerRef}
                />
              </Box>
            ))}
          </Box>
        )}

        {!pdfUrl && isRendered && annotations && annotations.length > 0 && (
          <Box
            data-annotation={"true"}
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              zIndex: 20,
            }}
          >
            {annotations.map((annotation) => (
              <Box
                key={annotation.id}
                data-annotation={"true"}
                sx={{ pointerEvents: "auto" }}
              >
                <AnnotationOverlay
                  annotation={annotation}
                  onRemove={onRemoveAnnotation}
                  onMove={onMoveAnnotation}
                  onResize={onResizeAnnotation}
                  zoom={zoom}
                  containerRef={docContainerRef}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {isDragOver && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor:
              dragType === "annotation"
                ? `${theme.palette.secondary.main}15`
                : `${theme.palette.primary.main}15`,
            border: `4px dashed ${
              dragType === "annotation"
                ? theme.palette.secondary.main
                : theme.palette.primary.main
            }`,
            pointerEvents: "none",
            zIndex: 20,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              p: 4,
              backgroundColor: "white",
              borderRadius: 2,
              textAlign: "center",
            }}
          >
            <Typography
              variant={"h5"}
              sx={{
                color:
                  dragType === "annotation"
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main,
                fontWeight: 600,
                mb: 1,
              }}
            >
              {dragType === "annotation"
                ? "Drop annotation here"
                : "Drop field here"}
            </Typography>
            <Typography variant={"body2"} color={"text.secondary"}>
              {dragType === "annotation"
                ? "Release to place the annotation at this location"
                : "Release to place the field at this location on the document"}
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default DocumentPreviewCanvas;
